/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B000180 銀行照会
 */

import { Column, Query } from "@material-table/core";
import { catchRestError } from "clients/rest";
import { B000180Find, B000180FindRequest } from "clients/rest/b000/B000180";
import {
  SText,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
  msgState,
} from "components";
import { labels } from "messages/labels";
import { createRef, useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { mapIds } from "services";
import { focusState, SDialogContext, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  ginkoCdFrom: string;
  ginkoShitenCdFrom: string;
  ginkoCdTo: string;
  ginkoShitenCdTo: string;
  ginkoNm: string;
  shitenNm: string;
  ginkoKana: string;
  shitenKana: string;
}>;

// テーブル定義
type Data = Partial<{
  gyoNo: number;
  ginkoCd: string;
  ginkoshitenCd: string;
  hyojiginkoCd: string;
  ginkoNm: string;
  ginkoKana: string;
  shitenNm: string;
  shitenKana: string;
  hyojiginkoNm: string;
  hyojiginkoKana: string;
  sakujoyoteiKbn: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  ginkoCdFrom: "",
  ginkoShitenCdFrom: "",
  ginkoCdTo: "",
  ginkoShitenCdTo: "",
  ginkoNm: "",
  shitenNm: "",
  ginkoKana: "",
  shitenKana: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "3rem", align: "right" },
  { field: "hyojiginkoCd", title: "銀行コード", width: "6rem" },
  { field: "hyojiginkoNm", title: "銀行名", width: "10rem" },
  { field: "hyojiginkoKana", title: "カナ名", width: "10rem" },
  { field: "ginkoCd", hidden: true },
  { field: "ginkoshitenCd", hidden: true },
  { field: "ginkoNm", hidden: true },
  { field: "ginkoKana", hidden: true },
  { field: "shitenNm", hidden: true },
  { field: "shitenKana", hidden: true },
  { field: "sakujoyoteiKbn", hidden: true },
];

const focusId = "B000180";

export const B000180 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};
const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするRef
  const isDefaultPage = useRef(false);
  // ダイアログの開閉を行う関数
  const handleSubmit = useContext(SDialogContext);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス関数
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();

  useEffect(() => {
    focus(1);
  }, []);

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }
      // 検索フォームのデータを取得する
      const fields = methods.getValues();

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B000180FindRequest = {
        ginkoCdFrom: fields.ginkoCdFrom,
        ginkoshitenCdFrom: fields.ginkoShitenCdFrom,
        ginkoCdTo: fields.ginkoCdTo,
        ginkoshitenCdTo: fields.ginkoShitenCdTo,
        ginkoNm: fields.ginkoNm,
        ginkoKana: fields.ginkoKana,
        shitenNm: fields.shitenNm,
        shitenKana: fields.shitenKana,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        sort: ["ginkoCd,asc"],
      };
      // API呼出し
      B000180Find(param)
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({ data: [], page: 0, totalCount: 0 });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (data: Data) => {
    handleSubmit(data);
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="銀行照会"
        gridTemplateColumns="8rem 6rem 4rem 1rem 6rem 4rem 2rem"
      >
        <SLabel>銀行コード</SLabel>
        <SNumberField name="ginkoCdFrom" code size={4} {...register(1)} />
        <SNumberField name="ginkoShitenCdFrom" code size={3} {...register(2)} />
        <SText>～</SText>
        <SNumberField name="ginkoCdTo" code size={4} {...register(3)} />
        <SNumberField name="ginkoShitenCdTo" code size={3} {...register(4)} />
        <SLabel gridColumn="1">銀行名</SLabel>
        <STextField
          name="ginkoNm"
          size={30}
          gridColumn="span 2"
          {...register(5)}
        />
        <SLabel gridColumn="1">支店名</SLabel>
        <STextField
          name="shitenNm"
          size={30}
          gridColumn="span 2"
          {...register(6)}
        />
        <SLabel gridColumn="1">銀行カナ名</SLabel>
        <STextField
          name="ginkoKana"
          size={30}
          gridColumn="span 2"
          {...register(7)}
        />
        <SLabel gridColumn="1">支店カナ名</SLabel>
        <STextField
          name="shitenKana"
          size={30}
          gridColumn="span 2"
          {...register(8)}
        />
        <SNormalButton
          label={labels.buttons.search}
          gridRow="6"
          gridColumn="7"
          {...register(9)}
          onClick={handleClickSearchButton}
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          handleDoubleClick={handleDoubleClickTableRow}
        />
      </SFrame>
    </FormProvider>
  );
};
